import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
} from 'react';
import { Itinerary } from 'generated/graphql';
import { useDisplayedItinerariesQuery } from 'clientState/providers/DisplayedItinerariesProvider';
import { useSearchResponseQuery } from 'clientState/providers/SearchResponseProvider';

type FavoritesContextProps = {
  favorites: Itinerary[];
  addFavorite: (item: string | undefined) => void;
  removeFavorite: (item: string | undefined) => void;
};

const DEFAULT_FAVORITES_STATE: FavoritesContextProps = {
  favorites: [],
  addFavorite: () => {},
  removeFavorite: () => {},
};

const FavoritesContext = createContext<FavoritesContextProps>(
  DEFAULT_FAVORITES_STATE
);

const FavoritesListProvider: React.FC = ({ children }) => {
  const [favorites, setFavorites] = useState<Itinerary[]>([]);
  const { data } = useSearchResponseQuery();
  const { itineraries } = useDisplayedItinerariesQuery();

  const addFavorite = useCallback(
    (itineraryId: string | undefined) => {
      const itinerary = itineraries.find(
        (itinerary) => itinerary.id === itineraryId
      );
      setFavorites([...favorites, itinerary]);
    },
    [favorites, itineraries, setFavorites]
  );

  const removeFavorite = useCallback(
    (itineraryId: string | undefined) => {
      setFavorites(
        favorites.filter((favorite) => favorite?.id !== itineraryId)
      );
    },
    [favorites, setFavorites]
  );

  useEffect(() => {
    setFavorites([]);
  }, [data, setFavorites]);

  return (
    <FavoritesContext.Provider
      value={{ favorites, addFavorite, removeFavorite }}
    >
      {children}
    </FavoritesContext.Provider>
  );
};

export default FavoritesListProvider;

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  return context;
};
